/**
 * compositionQuickEditPlacement.ts
 *
 * Placement math for `CompositionQuickEditPopover`. Extracted from the
 * component so the geometry is unit-testable without mounting the popover (and
 * so the component file stops growing).
 *
 * Always centred horizontally on the anchor. What changes is the vertical
 * origin:
 *
 *  - **Anchored** (the transcript surface): dropped below the anchor itself,
 *    flipping upward when the space below is too tight. The anchor there is a
 *    word / chip in a side panel, so "below" is empty space.
 *
 *  - **Cleared** (the canvas surface, PLAT-11090): dropped below `clearRect` —
 *    the selected graphic's projected box — instead of below the anchor. The
 *    canvas anchor is the pencil in the action pill, which the layer parks ~1/3
 *    INSIDE that box, so anchoring to it lands the popover squarely on top of
 *    the graphic being edited. Same drop-below behaviour, measured from the
 *    bottom of what the user is looking at.
 */

export interface PopoverPlacement {
  left: number;
  top: number;
  /**
   * Max-height set inline so the popover clamps to the viewport from its
   * actual top position. Without this the static `max-height: calc(100vh
   * - 32px)` on `Wrapper` allows the popover to extend past the bottom
   * edge whenever `top` isn't near 0 — e.g. when the pin-detail card
   * opens and pushes content below where the fold would be.
   */
  maxHeight: number;
}

/**
 * Kept in sync with the `Wrapper` styled-component's `width` declaration.
 * 280px in PLAT-10045; widened ~10% to 308px in PLAT-10644 for comfortable
 * header meta + field text, still clamping inside the viewport.
 */
const POPOVER_WIDTH = 308;
const POPOVER_ESTIMATED_HEIGHT = 320;
const MARGIN = 8;

/** The viewport box the popover has to stay inside. */
interface Viewport {
  width: number;
  height: number;
}

function readViewport(): Viewport {
  return { width: window.innerWidth, height: window.innerHeight };
}

/** Vertical clamp shared by both modes: keep `top` on screen, derive maxHeight. */
function clampVertically(
  top: number,
  viewport: Viewport,
): { top: number; maxHeight: number } {
  const clampedTop = Math.min(
    Math.max(MARGIN, top),
    Math.max(MARGIN, viewport.height - POPOVER_ESTIMATED_HEIGHT - MARGIN),
  );
  // Max-height = (viewport bottom) − (popover top) − (bottom margin). The
  // Wrapper's own `overflow-y: auto` takes over once content exceeds this, so
  // the pin-detail card + additional rows scroll inside the popover rather
  // than running off-screen.
  return {
    top: clampedTop,
    maxHeight: Math.max(120, viewport.height - clampedTop - MARGIN),
  };
}

/**
 * A viewport-pixel box the popover must open clear of — the selected graphic's
 * projected box on the canvas. Structural so callers can pass the
 * `computeElementViewportBox` result (or a DOMRect) without converting.
 */
export interface ClearRect {
  top: number;
  height: number;
}

/**
 * Vertical origin: the bottom of the box the popover has to clear. Without a
 * `clearRect` that's the anchor itself (transcript); with one it's the graphic
 * (canvas), which pushes the popover lower so it lands under the overlay
 * instead of across it.
 */
function resolveDropFrom(
  anchorRect: DOMRect,
  clearRect: ClearRect | null,
): { top: number; bottom: number } {
  if (clearRect && clearRect.height > 0) {
    return { top: clearRect.top, bottom: clearRect.top + clearRect.height };
  }
  return { top: anchorRect.top, bottom: anchorRect.bottom };
}

/**
 * Resolves the popover's fixed-position box. Pass `clearRect` (the selected
 * graphic's projected box) on the canvas surface so the popover drops below the
 * graphic rather than below the pencil sitting inside it; omit it on the
 * transcript surface, where the anchor is already clear of everything. A
 * zero-height `clearRect` (element box not resolvable) degrades to the anchor.
 */
export function computeQuickEditPlacement(
  anchor: HTMLElement,
  clearRect?: ClearRect | null,
): PopoverPlacement {
  const viewport = readViewport();
  const anchorRect = anchor.getBoundingClientRect();
  const dropFrom = resolveDropFrom(anchorRect, clearRect ?? null);

  /**
   * Extra vertical breathing room when opening upward — gives the popover
   * ~18px clearance above the pointer so the pencil / chip / underlined word
   * doesn't visually crowd the popover.
   */
  const UPWARD_GAP = MARGIN + 10;

  const spaceBelow = viewport.height - dropFrom.bottom;
  const openUpward =
    spaceBelow < POPOVER_ESTIMATED_HEIGHT &&
    dropFrom.top > POPOVER_ESTIMATED_HEIGHT;

  const rawTop = openUpward
    ? Math.max(MARGIN, dropFrom.top - POPOVER_ESTIMATED_HEIGHT - UPWARD_GAP)
    : Math.min(
        viewport.height - POPOVER_ESTIMATED_HEIGHT - MARGIN,
        dropFrom.bottom + MARGIN,
      );

  // Horizontally center the popover on the ANCHOR's center (not the clear
  // rect) — the anchor is the thing the user clicked, and a full-width
  // graphic's centre would drift the popover away from it. Then clamp so the
  // popover stays inside the viewport with an 8px gutter: anchoring
  // left-edge-to-anchor.left puts the popover off-screen when the anchor
  // (e.g. a transcript word or the hover-menu pill) sits near the right
  // edge of the right transcript panel.
  const anchorCenterX = anchorRect.left + anchorRect.width / 2;
  const left = Math.min(
    Math.max(MARGIN, anchorCenterX - POPOVER_WIDTH / 2),
    viewport.width - POPOVER_WIDTH - MARGIN,
  );

  const { top, maxHeight } = clampVertically(rawTop, viewport);
  return { left, top, maxHeight };
}
